import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import React, { useEffect, useState } from "react";
import { IAuthenticationCode, IHandleSubmit } from "@interfaces";
import { StyledBorderButton, StyledButton, StyledError, StyledForm, StyledLink } from "@components";
import { IHandleChange, IHandleBlur } from "@interfaces";
import { PATH_LOGIN, PATH_SIGNUP } from "@routes";
import { NavLink } from "react-router-dom";
import { authApi } from "@api";
import { ErrorToast } from "../toasts/ErrorToast";
import { useIsLoggedIn, useShouldVerifyEmail } from "@redux";
import { theme, useLogoutHandler } from "@utilities";
import { useOTPInput } from "src/hooks/useOTPInput";

interface AuthenticationFormProps {
    values: IAuthenticationCode;
    handleChange: IHandleChange;
    handleBlur: IHandleBlur;
    handleSubmit: IHandleSubmit;
    isSubmitting: boolean;
    constantText: string;
    constantContact: string;
    enteredEmail: string;
    errors: { [key: string]: string };
    touched: { [key: string]: boolean };
}

export const AuthenticationComponent = (props: AuthenticationFormProps) => {
    const {
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        constantText,
        constantContact,
        enteredEmail,
        errors,
        touched,
    } = props;
    const navigate = useNavigate();
    const isLoggedIn = useIsLoggedIn();
    const shouldVerifyEmail = useShouldVerifyEmail();
    const handleLogout = useLogoutHandler();
    const [countdown, setCountdown] = useState(shouldVerifyEmail ? 0 : 60);

    const { handlePaste, handleInputChange } = useOTPInput(values.otp, (newValue) =>
        handleChange({ target: { name: "otp", value: newValue } } as any),
    );

    useEffect(() => {
        // Countdown timer logic
        if (countdown > 0) {
            const timer = setInterval(() => setCountdown(countdown - 1), 1000);
            return () => clearInterval(timer); // Clean up the interval on unmount
        }
    }, [countdown]);

    const handleBackToLogin = async (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if (isLoggedIn) {
            await handleLogout();
            return;
        }
        navigate(PATH_LOGIN);
    };

    const handleResendCode = async () => {
        try {
            await authApi.resendVerificationOtp();
        } catch (err: any) {
            ErrorToast({ Message: err.data?.title || "An error occurred" });
        } finally {
            setCountdown(60);
        }
    };

    return (
        <>
            <StyledWrapper onSubmit={handleSubmit}>
                <h2 className="heading">Enter Authentication Code</h2>
                <p className="subHeading">
                    Enter the 5-digit that we have sent via email to
                    <span> {enteredEmail}</span>
                </p>
                <StyledCodeContainer>
                    {
                        <StyledCodeInput
                            type="text"
                            inputMode="numeric" // Mobile-friendly numeric keypad
                            pattern="\d*" // Accept digits only
                            maxLength={5} // Limit input to one digit per field
                            hideCaret={values.otp.length === 5}
                            onChange={(e) => handleInputChange(e)}
                            onBlur={handleBlur}
                            onPaste={handlePaste}
                            value={values.otp || ""}
                        />
                    }
                </StyledCodeContainer>
                {errors.otp && touched.otp && (
                    <StyledError className="error">{errors.otp}</StyledError>
                )}
                <StyledButton
                    $borderRadius={theme.borderRadius.small}
                    color={theme.gradients.button}
                    className="send_link"
                    type="submit"
                >
                    Continue
                </StyledButton>
                {/* todo need to add counter logic when integrating forgot password flow */}
                <StyledLine>
                    Didn’t receive a code?
                    {countdown > 0 ? (
                        ` Resend Code in ${countdown}s`
                    ) : (
                        <ResendButton type="button" onClick={() => handleResendCode()}>
                            Resend Code
                        </ResendButton>
                    )}
                </StyledLine>
                <StyledBorderButton onClick={handleBackToLogin}>
                    {isLoggedIn ? "Log out" : "Back to Login"}
                </StyledBorderButton>
                {!isLoggedIn && (
                    <>
                        <StyledLink>
                            Don't have an account? <NavLink to={PATH_SIGNUP}> Sign up</NavLink>, to
                            start saving today.
                        </StyledLink>
                        <StyledLine className="contact">{constantContact}</StyledLine>
                        <StyledLine>{constantText}</StyledLine>
                    </>
                )}
            </StyledWrapper>
        </>
    );
};

const StyledWrapper = styled(StyledForm)`
    .heading {
        font-size: 28px;
        font-style: normal;
        font-weight: 700;
        line-height: 36px;
        letter-spacing: 0em;
        text-align: center;
        margin-bottom: 18px;
    }

    .subHeading {
        text-align: center;
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        color: ${(props) => props.theme.colors.gray_2};
    }
    .subHeading span {
        font-weight: 700 !important;
    }
    .send_link {
        margin-top: 30px;
    }
    .error {
        text-align: center;
    }
`;

const StyledLine = styled.div`
    font-weight: 100;
    font-size: 14px;
    color: ${(props) => props.theme.colors.gray_1};
    margin: 10px 0px;
`;

const StyledCodeContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 0px;
    margin-bottom: 20px;
`;

const StyledCodeInput = styled.input<{ hideCaret?: boolean }>`
    width: 200px;
    height: 50px;
    margin: 20px 0px 0px 0px;
    border-radius: 8px;
    border: 1px solid ${(props) => props.theme.colors.gray_2};
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0.6em;
    text-indent: 0.6em;
    caret-color: transparent;
    caret-color: ${({ hideCaret }) => (hideCaret ? "transparent" : "auto")};
    color: ${theme.colors.button_text1};
    transition: border-color 0.2s ease-in-out; // Smooth transition for border color change

    &:focus {
        outline: none;
        border: 2px solid ${theme.colors.main};
    }
`;

const ResendButton = styled.button`
    background: none;
    border: none;
    color: ${theme.colors.highlight};
    text-decoration: underline;
    font-size: inherit;
    cursor: pointer;
    padding: 0;
    margin-left: 5px;

    &:hover {
        color: ${theme.colors.hover}; // Optional: A darker shade of blue on hover
    }
`;
